import { AreaPathNode, NodeAttributes } from "./area-path-node";

export class AreaPathTree {
  private treeData: AreaPathNode[] = [];
  private uniqueId = 1;
  private focusedNode: AreaPathNode;
  private selectedNode: AreaPathNode;
  private areaPath: string;
  private isAreaPathInvalid = false;
  public selectedRange = 0;
  private visibleNodeIndices: number[] = [];
  // holds matched nodes while user is searching
  private matchedNodes: number[] = [];

  constructor(
    data: any,
    private selectOnlyLeafNodes: boolean,
    private initialNodePredicate: any,
    private expandedDepthLevel: number
  ) {
    let optionsObject: NodeAttributes = {
      title: undefined,
      isCollapsed: false,
      isVisible: false,
      isFocused: false,
      isSearched: false,
      parent: undefined,
      level: undefined,
      id: 0,
      nodeData: undefined
    };

    let virtualRootNode = new AreaPathNode(optionsObject);
    this.treeData.push(virtualRootNode);
    this.buildNodeData(data, 0, 0);
  }

  public getTreeData(): AreaPathNode[] {
    return this.treeData;
  }

  public getFocusedNode(): AreaPathNode {
    if (this.focusedNode) {
      return this.focusedNode;
    }
  }

  public applyFocus(node: AreaPathNode, updateSelectedNode: boolean): void {
    if (node) {
      node.isFocused = true;
      this.focusedNode = node;
      if (updateSelectedNode) {
        this.selectedNode = node;
      }
    }
  }

  public removeFocus(): void {
    if (this.focusedNode) {
      this.focusedNode.isFocused = false;
    }
  }

  public getSelectedNodeIndex(): number {
    if (this.selectedNode) {
      return this.selectedNode.id;
    }
  }

  public getSelectedNodeData(): string {
    if (this.selectedNode) {
      return this.selectedNode.nodeData;
    }
  }

  public constructAreaPath(node: AreaPathNode): string {
    let areaPath = "";
    while (true) {
      if (!node.id) {
        return areaPath;
      }

      areaPath = !!areaPath ? `${node.title}\\${areaPath}` : node.title;
      node = this.treeData[node.parent];
    }
  }

  public expandNode(): void {
    if (this.focusedNode) {
      this.focusedNode.isCollapsed = false;
      this.toggleVisibilityChildNodes(this.focusedNode, true);
    }
  }

  public collapseNode(): void {
    if (this.focusedNode) {
      if (!this.focusedNode.isCollapsed) {
        this.focusedNode.isCollapsed = true;
        this.toggleVisibilityChildNodes(this.focusedNode, false);
      } else {
        this.updateFocusAndAreaPath(this.focusedNode.parent);
      }
    }
  }

  public toggleVisibilityChildNodes(node: AreaPathNode, show: boolean): void {
    for (let index in node.children) {
      let childNode = this.treeData[node.children[index]];
      childNode.isVisible = show;
      if (!childNode.isCollapsed) {
        this.toggleVisibilityChildNodes(childNode, show);
      }
    }
  }

  public navigateToNextNode(): void {
    if (!this.focusedNode) {
      // set the focus to first node.
      // since node at index 0 is virtual/root, initial valid node starts at 1
      let firstValidNodeIndex = 1;
      this.updateFocusAndAreaPath(firstValidNodeIndex);
      return;
    }

    let nextIndex: number;
    for (let i = this.focusedNode.id + 1; i < this.treeData.length; i++) {
      if (this.treeData[i].isVisible) {
        nextIndex = i;
        break;
      }
    }

    this.updateFocusAndAreaPath(nextIndex);
  }

  public navigateToPreviousNode(): void {
    let prevIndex: number;
    for (let i = this.focusedNode.id - 1; i >= 0; i--) {
      if (this.treeData[i].isVisible) {
        prevIndex = i;
        break;
      }
    }

    this.updateFocusAndAreaPath(prevIndex);
  }

  public getAreaPath(): string {
    return this.areaPath;
  }

  public updateFocusedNodeAreaPath(
    areaPath: string,
    doNotAutoComplete: boolean,
    isUserSearching: boolean
  ): AreaPathNode {
    // if areaPath contains "\\", it is an autoComplete scenario else it is a search scenario
    if (areaPath && !isUserSearching) {
      return this.updateFocusedNodeAreaPathOnAutoComplete(areaPath, doNotAutoComplete);
    } else {
      return this.updateFocusedNodeAreaPathOnSearch(areaPath);
    }
  }

  private updateFocusedNodeAreaPathOnSearch(areaPath: string): AreaPathNode {
    let focusedNode: AreaPathNode = undefined;

    let searchText = areaPath;
    // handle cleared searchtext
    if (!searchText || searchText.trim() === "") {
      this.resetNodeData();
    } else {
      let searchTextParts = searchText.toLowerCase().split(" ");

      // find all nodes that matches the searchText
      let matchedNodes: number[] = [];
      this.treeData.forEach((node: AreaPathNode) => {
        if (node.id) {
          let titleLower = node.title.toLowerCase();
          if (searchTextParts.every((part) => titleLower.includes(part))) {
            matchedNodes.push(node.id);
          } else {
            this.treeData[node.id].isVisible = false;
            this.treeData[node.id].isSearched = false;
          }
        }
      });

      // show the nodes that matched and expand its ancestors all the way to the top!
      if (matchedNodes.length) {
        // focusedNode is the first node that matched searchText
        focusedNode = this.treeData[matchedNodes[0]];

        matchedNodes.forEach((id: number) => {
          this.treeData[id].isVisible = true;
          this.treeData[id].isSearched = true;
          let parent = this.treeData[id].parent;
          while (parent) {
            this.treeData[parent].isVisible = true;
            this.treeData[parent].isCollapsed = false;
            parent = this.treeData[parent].parent;
          }
        });

        this.matchedNodes = matchedNodes;
      }
    }

    // areaPath is always invalid in this scenario since we are just searching and have not selected any node yet.
    this.isAreaPathInvalid = true;
    return focusedNode;
  }

  private updateFocusedNodeAreaPathOnAutoComplete(areaPath: string, doNotAutoComplete: boolean): AreaPathNode {
    let focusedNode: AreaPathNode = undefined;

    if (areaPath) {
      let scopedSearchIndices = this.treeData[0].children;
      let visibleNodes: number[] = [];
      let areaPathNodes = areaPath.split("\\");
      for (let i = 0; i < areaPathNodes.length; i++) {
        // set the search scope of nodes. Should be searching just in its children nodes
        for (let index in scopedSearchIndices) {
          let nodeIndex = scopedSearchIndices[index];
          let currentActualNodeValue = this.treeData[nodeIndex].title.toLowerCase();
          let currentTypedNodeValue = areaPathNodes[i].toLowerCase();
          // look for complete match
          if (currentActualNodeValue == currentTypedNodeValue) {
            visibleNodes.push(nodeIndex);
            scopedSearchIndices = this.treeData[nodeIndex].children;
            break;
          }

          // look for partial match and autosuggest in the last node
          // doNotAutoComplete ensures that autosuggest is prompted only when when user is typing and not with initial load
          if (i == areaPathNodes.length - 1 && doNotAutoComplete) {
            if (currentActualNodeValue.substr(0, currentTypedNodeValue.length) == currentTypedNodeValue) {
              visibleNodes.push(nodeIndex);
              let selectedRange = currentActualNodeValue.length - currentTypedNodeValue.length;
              let suggestedValue = currentActualNodeValue.substr(currentTypedNodeValue.length, selectedRange);
              this.areaPath = `${areaPath}${suggestedValue}`;
              this.selectedRange = selectedRange;
              break;
            }
          }
        }
      }

      // check if the whole path matched
      if (visibleNodes.length == areaPathNodes.length) {
        this.visibleNodeIndices = visibleNodes;
        focusedNode = this.treeData[this.visibleNodeIndices[this.visibleNodeIndices.length - 1]];
        this.displayVisibleNodes();
      }
    }

    if (focusedNode) {
      this.isAreaPathInvalid = this.selectOnlyLeafNodes && focusedNode.children.length > 0;
    } else {
      this.isAreaPathInvalid = true;
    }

    return focusedNode;
  }

  // gets the index of the selected Node in reference to the matched Node list
  public getSelectedNodeIndexInMatchedNodes() {
    if (this.selectedNode.id) {
      return this.matchedNodes.indexOf(this.selectedNode.id);
    }
  }

  public updateFocusedNodeOnLoad(areaPath: string) {
    if (this.initialNodePredicate) {
      this.focusedNode = this.selectedNode
        ? this.updateFocusedNodeAreaPath(this.constructAreaPath(this.selectedNode), false, false)
        : undefined;
    } else {
      this.focusedNode = this.updateFocusedNodeAreaPath(areaPath, false, false);
    }

    if (this.focusedNode) {
      this.areaPath = this.constructAreaPath(this.focusedNode);
    }
    this.selectedNode = this.focusedNode;
  }

  public updateFocusedNode(areaPath: string): void {
    // remove focus if areaPath is empty or area path is set to incorrect
    if ((!areaPath || this.isAreaPathInvalid) && this.focusedNode) {
      this.removeFocus();
      this.focusedNode = undefined;
    }

    if (this.focusedNode && this.selectedNode) {
      // focused node might have changed due to mouse hover. we want to highlight selected node instead of focused node.
      if (this.selectedNode != this.focusedNode) {
        this.removeFocus();
        this.focusedNode = this.selectedNode;
      }

      this.selectedNode.isFocused = true;
      this.isAreaPathInvalid = false;
    } else {
      this.isAreaPathInvalid = true;
    }
  }

  public getAreaPathValidity(): boolean {
    return this.isAreaPathInvalid;
  }

  public setAreaPathValidity(isAreaPathInvalid: boolean): boolean {
    this.isAreaPathInvalid = isAreaPathInvalid;
    return this.isAreaPathInvalid;
  }

  private displayVisibleNodes(): void {
    this.visibleNodeIndices.forEach((index) => {
      this.treeData[index].isVisible = true;
      // parent should be expanded, if node is visible
      let parentIndex = this.treeData[index].parent;
      if (parentIndex) {
        this.treeData[parentIndex].isCollapsed = false;
        // all siblings should be visible, if node is visible
        this.treeData[parentIndex].children.forEach((sibling: number) => {
          this.treeData[sibling].isVisible = true;
        });
      }
    });
  }

  private updateFocusAndAreaPath(index: number): void {
    if (index) {
      this.removeFocus();
      this.applyFocus(this.treeData[index], true);

      if (!this.selectOnlyLeafNodes || this.treeData[index].children.length <= 0) {
        this.areaPath = this.constructAreaPath(this.treeData[index]);
      }
    }
  }

  /* resets the tree data to its original default state */
  private resetNodeData() {
    this.treeData.forEach((node: AreaPathNode) => {
      node.isVisible = false;
      node.isCollapsed = true;
      if (node.level <= this.expandedDepthLevel) {
        node.isVisible = true;
        if (node.parent) {
          this.treeData[node.parent].isCollapsed = false;
        }
      }

      node.isSearched = false;
    });
  }

  private buildNodeData(nodeObjects: [{}], parentId: number, level: number): void {
    if (!nodeObjects) {
      return;
    }

    let currentLevel = ++level;
    nodeObjects.forEach((node) => {
      if (!!node) {
        let isVisible = false;
        if (level <= this.expandedDepthLevel) {
          isVisible = true;
        }

        // delete child nodes from node
        let childNodes = (<any>node).childNodes;
        let nodeData = Object.keys(node).reduce((nodeData: any, key: any) => {
          if (key !== "childNodes") {
            nodeData[key] = (<any>node)[key];
          }

          return nodeData;
        }, {});

        let optionsObject: NodeAttributes = {
          title: (<any>node).title,
          isCollapsed: true,
          isVisible: isVisible,
          isSearched: false,
          isFocused: false,
          parent: parentId,
          level: currentLevel,
          id: this.uniqueId,
          nodeData: nodeData
        };

        // if a node is visible, it's parent, if exists should be expanded
        if (isVisible && parentId) {
          this.treeData[parentId].isCollapsed = false;
        }

        let treeNode = new AreaPathNode(optionsObject);
        this.treeData[parentId].children.push(this.uniqueId);
        let id = this.uniqueId++;
        this.treeData.push(treeNode);

        // find the node corresponding to given initial ap id
        if (this.initialNodePredicate && this.initialNodePredicate({ nodeData: nodeData })) {
          this.selectedNode = treeNode;
        }

        this.buildNodeData(childNodes, id, currentLevel);
      }
    });
  }
}
